import { useCallback, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from './sidebar';
import { Header } from './header';
import { ProtectedRoute } from '@/features/auth/components/protected-route';
import { cn } from '@/lib/utils';

// 需要固定布局（局部滚动）的路由
const FIXED_LAYOUT_ROUTES = ['/recruitment-pipeline'];

export function MainLayout() {
  const location = useLocation();
  // 移动端侧边栏开关状态
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  // 桌面端侧边栏收起状态
  const [isDesktopSidebarCollapsed, setIsDesktopSidebarCollapsed] =
    useState(false);

  // 判断当前路由是否需要固定布局
  const isFixedLayout = FIXED_LAYOUT_ROUTES.some((route) =>
    location.pathname.startsWith(route)
  );

  // 切换侧边栏显示状态（移动端）
  const handleToggleSidebar = useCallback(() => {
    setIsMobileSidebarOpen((prev) => !prev);
  }, []);

  // 关闭侧边栏（移动端）
  const handleCloseSidebar = useCallback(() => {
    setIsMobileSidebarOpen(false);
  }, []);

  // 切换侧边栏收起状态（桌面端）
  const handleToggleCollapse = useCallback(() => {
    setIsDesktopSidebarCollapsed((prev) => !prev);
  }, []);

  return (
    <ProtectedRoute>
      <div
        className={cn(
          'flex p-3 gap-2',
          isFixedLayout
            ? 'h-screen overflow-x-auto overflow-y-hidden'
            : 'min-h-screen overflow-auto'
        )}
        style={{ backgroundColor: '#F0F4F8' }}
      >
        {/* 桌面端侧边栏 - 固定定位 */}
        <Sidebar
          className="hidden lg:flex fixed left-3 top-3 z-30 rounded-xl shadow-sm"
          style={{ height: 'calc(100vh - 24px)' }}
          isCollapsed={isDesktopSidebarCollapsed}
          onToggleCollapse={handleToggleCollapse}
        />

        {/* 移动端遮罩 */}
        {isMobileSidebarOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/40 lg:hidden animate-fade-in"
            onClick={handleCloseSidebar}
            aria-hidden="true"
          />
        )}

        {/* 移动端侧边栏 */}
        <Sidebar
          className={cn(
            'fixed inset-y-0 left-0 z-50 w-64 border-r bg-card shadow-lg transition-all duration-300 ease-out lg:hidden',
            isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'
          )}
          isMobile
          onClose={handleCloseSidebar}
        />

        {/* Main Content - 为侧边栏留出空间 */}
        <div
          className={cn(
            'flex flex-1 flex-col h-full transition-all duration-300 relative',
            isDesktopSidebarCollapsed ? 'lg:ml-[4.25rem]' : 'lg:ml-[15.75rem]'
          )}
        >
          {/* 固定的Header */}
          <div
            className="sticky top-0 z-20 px-4 pb-2"
            style={{ backgroundColor: '#F0F4F8' }}
          >
            <Header onToggleSidebar={handleToggleSidebar} />
          </div>

          {/* 页面内容 - 根据布局模式决定滚动方式 */}
          <main
            className={cn(
              'flex-1 px-4 flex flex-col min-h-0',
              isFixedLayout ? 'overflow-hidden' : 'overflow-auto'
            )}
            style={{ backgroundColor: '#F0F4F8' }}
          >
            <div className="page-enter pb-4 flex-1 flex flex-col min-h-0">
              <div
                className={cn(
                  'rounded-xl border border-[#E5E7EB] bg-white shadow-sm p-4 lg:p-6 flex-1 flex flex-col min-h-0',
                  isFixedLayout ? 'overflow-hidden' : 'overflow-auto'
                )}
              >
                <Outlet />
              </div>
            </div>
          </main>
        </div>
      </div>
    </ProtectedRoute>
  );
}
